import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { Box } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { useFetchCreds } from '~hooks/fetchCreds'
import PageNotFoundScreen from '~pages/PageNotFoundScreen'
import { RootState } from '~redux/rootReducer'
import { SkinEnum } from '~types'

const RequireStaffMemberAuth: React.FC<{
  organizationHandle?: string
  children: React.ReactElement
}> = ({ organizationHandle, children }) => {
  const isAuthenticated = useSelector((state: RootState) => state?.auth.isAuthenticated)
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const user = useSelector((state: RootState) => state?.auth.user)
  const location = useLocation()
  const { fetchUserCredentials } = useFetchCreds()
  const initialRender = useRef(true)
  const [userIsStaff, setUserIsStaff] = useState<boolean | null>(null)

  const fetchAuth = async (): Promise<void> => {
    await fetchUserCredentials(userId || null, user || null)
    initialRender.current = false
  }

  const checkUserIsStaffer = async (): Promise<void> => {
    if (!organizationHandle || !userId) return
    const { identity: organizationIdentity } = await apiZodios.core_getIdentity({
      handle: organizationHandle,
    })
    const { identities: identity0s } = await apiZodios.core_listStaffers({
      user_id: userId,
      identity_ids: [organizationIdentity.id],
    })
    if (identity0s.some((identity) => identity.id === user?.identity_id)) {
      setUserIsStaff(true)
    } else {
      setUserIsStaff(false)
    }
  }

  useEffect(() => {
    if (initialRender.current && !isAuthenticated) {
      fetchAuth()
    } else {
      initialRender.current = false
    }
  }, [])

  useEffect(() => {
    void checkUserIsStaffer()
  }, [organizationHandle])

  if (!userId || (!initialRender.current && !isAuthenticated)) {
    return <Navigate to='/' state={{ from: location }} replace />
  }

  if (user?.skin === SkinEnum.enum.Casual) {
    return <PageNotFoundScreen permissionsError={false} />
  } else if (user?.skin === SkinEnum.enum.Professional && userIsStaff === false) {
    return <PageNotFoundScreen permissionsError={true} />
  } else if (userIsStaff === true) {
    return children
  } else {
    return <Box />
  }
}

export default RequireStaffMemberAuth
